import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { IndexDetail, CrudEventEnum, EntityTypeEnum, EntityStatus, CrudActions, WorkflowActions } from 'api'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { ActionsButtonProps } from 'view/components/datagrid'
import { SplitButtonDropdown, ActionItem } from 'view/components/menus'
import { IndexEditDialog } from '../IndexDialog'
import { NotificationActions, ShowDialogArgs } from 'store/notification'
import { CorrelatedResponseCallback, NotificationType } from 'store/notification/notificationTypes'
import { createLinkedCollateralsLengthGetterSelector } from 'selectors/indexSelectors'
import { Translation, Locale } from 'locale'
import { IndexViewDialog } from '../IndexDialog/IndexViewDialog'

export type IndexActionsButtonProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps, ActionsButtonProps<IndexDetail> {}

interface StateProps {
  getLinkedCollateralsLength: (indexId: string) => number
}

interface DispatchProps {
  onShowIndexDialog: (entityTypeEnum: EntityTypeEnum, entityId: string, correlatedResponseCallback?: CorrelatedResponseCallback) => unknown
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
}

const mapStateToProps = createStructuredSelector({
  getLinkedCollateralsLength: createLinkedCollateralsLengthGetterSelector
})

const mapDispatchToProps = {
  onShowIndexDialog: WorkflowActions.createEditWorkflow,
  onShowDialog: NotificationActions.showDialog
}

const canEditDetails = (item: IndexDetail) => item.availableEvents.includes(CrudEventEnum.UPSERT)
const canSuspendDetails = (item: IndexDetail) =>
  item.index.status === EntityStatus.ENABLED && item.availableEvents.includes(CrudEventEnum.UPSERT)
const canActivateDetails = (item: IndexDetail) =>
  item.index.status === EntityStatus.DISABLED && item.availableEvents.includes(CrudEventEnum.UPSERT)
const canDeleteDetails = (item: IndexDetail, linkedCollateralsLength: number) =>
  item.availableEvents.includes(CrudEventEnum.DELETE) && linkedCollateralsLength === 0

export const IndexActionsButtonComponent: React.FunctionComponent<IndexActionsButtonProps> = props => {
  const { items, isGroupRow, getLinkedCollateralsLength, onShowIndexDialog, onShowDialog, showModal } = props

  const viewItems = items.filter(item => !canEditDetails(item))
  const editItems = items.filter(item => canEditDetails(item))
  const suspendItems = items.filter(item => canSuspendDetails(item))
  const activateItems = items.filter(item => canActivateDetails(item))
  const deleteItems = items.filter(item => canDeleteDetails(item, getLinkedCollateralsLength(item.index.id)))

  const handleEditItemDialog = (entityId: string) => {
    const correlatedResponseCallback = (workflowId: string) => {
      showModal(<IndexEditDialog workflowId={workflowId} entityId={entityId} />)
    }

    onShowIndexDialog(EntityTypeEnum.INDEX, entityId, correlatedResponseCallback)
  }

  const handleViewItemDialog = (entityId: string) => showModal(<IndexViewDialog entityId={entityId} />)

  const handleSuspend = (suspendIndices: IndexDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_TRADING)

    const messageText =
      suspendIndices.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_SINGLE_INDEX, {
            name: suspendIndices[0].index.code
          })
        : Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_MULTI_INDEX, { count: suspendIndices.length })

    const suspendActions = suspendIndices.map(suspendItem =>
      CrudActions.indexUpsert({
        ...suspendItem.index,
        status: EntityStatus.DISABLED
      })
    )

    onShowDialog({
      type: NotificationType.Confirm,
      titleText,
      messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: suspendActions
      }
    })
  }

  const handleActivate = (activateIndices: IndexDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_TRADING)

    const messageText =
      activateIndices.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_SINGLE_INDEX, {
            name: activateIndices[0].index.code
          })
        : Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_MULTI_INDEX, { count: activateIndices.length })

    const activateActions = activateIndices.map(activateItem =>
      CrudActions.indexUpsert({
        ...activateItem.index,
        status: EntityStatus.ENABLED
      })
    )

    onShowDialog({
      type: NotificationType.Confirm,
      titleText,
      messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: activateActions
      }
    })
  }

  const handleDelete = (deleteIndices: IndexDetail[]) => {
    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_DELETE_INDEX, { count: deleteIndices.length })

    const messageText =
      deleteIndices.length === 1
        ? Locale.translate(Translation.SC_PROMPT_MSG_DELETE_SINGLE_INDEX, {
            name: deleteIndices[0].index.code
          })
        : Locale.translate(Translation.SC_PROMPT_MSG_DELETE_MULTI_INDEX, { count: deleteIndices.length })

    const deleteActions = deleteIndices.map(deleteItem => CrudActions.indexDelete(deleteItem.index.id))

    onShowDialog({
      type: NotificationType.Confirm,
      titleText,
      messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: deleteActions
      }
    })
  }

  const getLabel = (baseLabel: Translation, count: number) => {
    const countLabel = isGroupRow ? ` (${count})` : ``
    return `${Locale.translate(baseLabel)}${countLabel}`
  }

  const actions: ActionItem[] = [
    {
      label: getLabel(Translation.TC_ACTION_VIEW_DETAILS, 1),
      onClick: () => handleViewItemDialog(items[0].index.id),
      enabled: viewItems.length === 1
    },
    {
      label: getLabel(Translation.TC_ACTION_EDIT_DETAILS, 1),
      onClick: () => handleEditItemDialog(items[0].index.id),
      enabled: editItems.length === 1
    },
    {
      label: getLabel(Translation.TC_ACTION_SUSPEND, suspendItems.length),
      onClick: () => handleSuspend(suspendItems),
      enabled: suspendItems.length > 0
    },
    {
      label: getLabel(Translation.TC_ACTION_ACTIVATE, activateItems.length),
      onClick: () => handleActivate(activateItems),
      enabled: activateItems.length > 0
    },
    {
      label: getLabel(Translation.TC_ACTION_DELETE, deleteItems.length),
      onClick: () => handleDelete(deleteItems),
      enabled: deleteItems.length > 0
    }
  ]
  return <SplitButtonDropdown actions={actions} maxButtons={1} autoHideEllipsis={true} />
}

export const IndexActionsButton = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(IndexActionsButtonComponent)
)
